<template>
  <div class="dkc" :class="`dkc--${color}`" @click="$emit('click')">
    <div class="dkc-head">
      <span class="dkc-lbl">{{ label }}</span>
      <div class="dkc-ico" :style="icoStyle">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path :d="iconPath" />
          <circle v-if="withCircle" cx="12" cy="12" r="3" />
        </svg>
      </div>
    </div>
    <div class="dkc-val mono">{{ value }}</div>
    <div class="dkc-ft">
      <span class="dkc-delta" :class="pos ? 'up' : 'dn'">{{ pos ? '▲' : '▼' }} {{ delta }}</span>
      <svg class="dkc-spark" viewBox="0 0 100 28" preserveAspectRatio="none">
        <defs>
          <linearGradient :id="`sg-${uid}`" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" :stop-color="colorHex" stop-opacity="0.35" />
            <stop offset="100%" :stop-color="colorHex" stop-opacity="0" />
          </linearGradient>
        </defs>
        <path :d="fillD" :fill="`url(#sg-${uid})`" />
        <polyline :points="lineP" fill="none" :stroke="colorHex" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
      </svg>
    </div>
  </div>
</template>

<script>
let uid = 0
export default {
  name: 'DashKpiCard',
  props: {
    label: String,
    value: String,
    delta: String,
    pos: { type: Boolean, default: true },
    color: { type: String, default: 'blue' },
    iconPath: String,
    withCircle: { type: Boolean, default: false },
    spark: { type: Array, default: () => [3, 5, 4, 7, 6, 8, 10] },
  },
  data() { return { uid: ++uid } },
  computed: {
    colorHex() {
      return { blue: '#318feb', orange: '#E86A10', gold: '#F5B935', green: '#28C76F' }[this.color] || '#318feb'
    },
    icoStyle() {
      const rgb = { blue: '49,143,235', orange: '232,106,16', gold: '245,185,53', green: '40,199,111' }[this.color]
      return { background: `rgba(${rgb},.15)`, color: `rgb(${rgb})` }
    },
    pts() {
      const d = this.spark, mn = Math.min(...d), mx = Math.max(...d), r = mx - mn || 1
      return d.map((v, i) => ({ x: (i / (d.length - 1)) * 96 + 2, y: 25 - ((v - mn) / r) * 22 }))
    },
    lineP() { return this.pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') },
    fillD() {
      const p = this.pts
      const line = p.map(x => `${x.x.toFixed(1)},${x.y.toFixed(1)}`).join(' L ')
      return `M${p[0].x.toFixed(1)},27 L${line} L${p[p.length - 1].x.toFixed(1)},27Z`
    },
  },
}
</script>

<style>
.dkc {
  background: linear-gradient(180deg, rgba(22,29,43,.8), rgba(16,21,31,.9));
  border: 1px solid var(--wr-line-2);
  border-radius: 16px;
  padding: 20px 22px;
  position: relative;
  overflow: hidden;
  transition: transform .2s, border-color .2s;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.dkc::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; border-radius: 16px 16px 0 0;
}
.dkc--blue::before  { background: linear-gradient(90deg, #318feb, #3dd5e0); }
.dkc--orange::before { background: linear-gradient(90deg, #E86A10, #F5B935); }
.dkc--gold::before  { background: linear-gradient(90deg, #F5B935, #FFD56B); }
.dkc--green::before { background: linear-gradient(90deg, #28C76F, #75e3a6); }
.dkc:hover { transform: translateY(-2px); border-color: var(--wr-line-strong); }
.dkc-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
.dkc-lbl { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; color: var(--wr-mute); }
.dkc-ico { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dkc-val { font-family: 'JetBrains Mono', monospace; font-size: 32px; font-weight: 900; letter-spacing: -1px; font-variant-numeric: tabular-nums; color: var(--wr-text); margin-bottom: 14px; line-height: 1; }
.dkc-ft { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.dkc-delta { font-size: 12px; font-weight: 700; font-family: 'JetBrains Mono', monospace; white-space: nowrap; flex-shrink: 0; }
.dkc-delta.up { color: #28C76F; }
.dkc-delta.dn { color: #FF5A6B; }
.dkc-spark { flex: 1; height: 28px; display: block; overflow: visible; }
</style>
